import React from "react";
import { useTranslation } from "react-i18next";
import { Clock, MoreHorizontal } from "lucide-react";
import { useAppSelector } from "hooks";
import Label from "./Label";
import MemberAvatar from "./MemberAvatar";
import CardBadges, { DueDateBadge } from "./CardBadges";
import { ProjectOutcome } from "containers/project-board/types";
import {
  idleDaysLabel,
  isCardRotting,
  prefersReducedMotion,
} from "features/project-board/project-motion";

interface ProjectCardProps {
  cardId: string;
  onOpen?: () => void;
  /** Shows a «...» button (phones only) that asks to move the card without dragging. */
  onMove?: () => void;
  isDragging?: boolean;
}

const OUTCOME_TONE: Record<ProjectOutcome, "won" | "lost" | "deleted"> = {
  WON: "won",
  LOST: "lost",
  DELETED: "deleted",
};

function formatEur(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  return new Intl.NumberFormat("lt-LT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(value));
}

/**
 * Trello-style card. Connected: it selects ONLY its own card object, so it
 * re-renders just when that card changes (not when siblings move). Wrapped in
 * React.memo so a column re-render (its id array changed) doesn't re-render
 * cards whose data is unchanged.
 */
function ProjectCard({ cardId, onOpen, onMove, isDragging }: ProjectCardProps) {
  const { t } = useTranslation();
  const card = useAppSelector((s) => s.projectBoardReducer.cardsById[cardId]);
  const currentUserId = useAppSelector((s) => s.authReducer.role?.user);
  const rottingDays = useAppSelector((s) => {
    const colId = s.projectBoardReducer.cardsById[cardId]?.projectBoardColumn;
    return colId
      ? s.projectBoardReducer.columnsById[colId]?.rottingDays
      : null;
  });

  if (!card) return null;

  // An optimistic card (temp id, not yet created on the server) must not be
  // clickable — opening it would navigate to /project/temp-… (a broken view).
  const interactive = !card.__optimistic;
  const canMove = !!onMove && interactive;

  const customerLine =
    card.customer && (card.customer.name || card.customer.externalCode)
      ? `${card.customer.name || ""}${
          card.customer.externalCode ? ` [${card.customer.externalCode}]` : ""
        }`.trim()
      : null;

  const valueLabel = formatEur(card.valueEur);
  const outcome = card.outcome || null;
  const showHeader =
    card.isImportant || card.isResolved || !!outcome || !!card.dueDate;

  const rotting =
    !outcome && isCardRotting(card.lastActivityAt, rottingDays);
  const idleDays = rotting ? idleDaysLabel(card.lastActivityAt) : null;
  const reduceMotion = prefersReducedMotion();

  const clickableCard = (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onOpen : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (onOpen) onOpen();
              }
            }
          : undefined
      }
      title={
        rotting && idleDays !== null
          ? t("project_rotting_idle", {
              defaultValue: "Idle for {{n}} days",
              n: idleDays,
            })
          : undefined
      }
      className={`group rounded-lg border px-3 py-2.5 text-left ${
        reduceMotion
          ? ""
          : "transition-[transform,box-shadow,border-color,background-color] duration-[400ms] ease-in-out"
      } ${
        isDragging
          ? "scale-[1.03] border-[var(--p-color-border-focus)] shadow-[0_8px_16px_rgba(0,0,0,0.15)] ring-1 ring-[var(--p-color-border-focus)]"
          : rotting
            ? "border-[var(--p-color-border)] border-l-4 border-l-[var(--p-color-border-critical)] bg-[var(--p-color-bg-surface-critical)] shadow-sm hover:shadow"
            : "border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] shadow-sm hover:shadow"
      } ${card.__optimistic ? "cursor-default opacity-60" : "cursor-pointer"}`}
    >
      {/* Header: status labels on the left, due date pinned to the top-right. */}
      {(showHeader || rotting) && (
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <div className="flex flex-wrap gap-1">
            {card.isImportant && (
              <Label tone="important">
                {t("important", { defaultValue: "Important" })}
              </Label>
            )}
            {outcome ? (
              <Label tone={OUTCOME_TONE[outcome]}>
                {t(`project_outcome_${outcome.toLowerCase()}`, {
                  defaultValue: outcome,
                })}
              </Label>
            ) : (
              card.isResolved && (
                <Label tone="resolved">
                  {t("done", { defaultValue: "Done" })}
                </Label>
              )
            )}
            {rotting && (
              <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold text-[var(--p-color-text-critical)]">
                <Clock size={12} strokeWidth={2} />
                {/* Said in words: a touch screen shows no title tooltip. */}
                {idleDays !== null
                  ? t("project_rotting_idle", {
                      defaultValue: "Idle for {{n}} days",
                      n: idleDays,
                    })
                  : t("project_rotting", { defaultValue: "Idle" })}
              </span>
            )}
          </div>
          <DueDateBadge card={card} />
        </div>
      )}

      {customerLine ? (
        <>
          <div className="text-[14px] font-semibold leading-snug text-[var(--p-color-text)]">
            {customerLine}
          </div>
          {card.name && (
            <div className="mt-0.5 text-[13px] leading-snug text-[var(--p-color-text-secondary)]">
              {card.name}
            </div>
          )}
        </>
      ) : (
        card.name && (
          <div className="text-[14px] font-semibold leading-snug text-[var(--p-color-text)]">
            {card.name}
          </div>
        )
      )}

      {valueLabel && (
        <div className="mt-1 text-[13px] font-semibold tabular-nums text-[var(--p-color-text)]">
          {valueLabel}
        </div>
      )}

      {card.contactName && (
        <div className="mt-1 text-[12px] leading-snug text-[var(--p-color-text-secondary)]">
          {card.contactName}
          {card.contactPhone ? ` · ${card.contactPhone}` : ""}
        </div>
      )}

      <div
        className={`flex items-end justify-between ${
          canMove ? "max-md:min-h-9 max-md:pr-8" : ""
        }`}
      >
        <div className="min-w-0 flex-1">
          <CardBadges card={card} currentUserId={currentUserId} />
        </div>
        {card.user && card.user.name && (
          <div className="ml-2 mt-2 shrink-0">
            <MemberAvatar name={card.user.name} />
          </div>
        )}
      </div>
    </div>
  );

  // The «...» button sits beside the clickable card, not inside it: a button
  // nested in role="button" is not announced by screen readers.
  return (
    <div className="relative">
      {clickableCard}
      {canMove && (
        <button
          type="button"
          aria-label={t("move", { defaultValue: "Move" })}
          onClick={onMove}
          className="absolute bottom-0 right-0 rounded p-3.5 text-[var(--p-color-text-secondary)] md:hidden"
        >
          <MoreHorizontal size={16} strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

export default React.memo(ProjectCard);
